import { Color } from '../tokens/colors.js'

function getColorForString(value: string) {
  const c = ['neutral', 'green', 'purple', 'orange', 'red'] as const
  let hash = 0
  for (let i = 0; i < value.length; i++) {
    hash = value.charCodeAt(i) + hash * 31
  }
  hash = Math.abs(hash)

  return c[hash % c.length]
}

function getChartColorForNumber(value: number, mono: boolean) {
  return (
    mono ? `chartMono${(value % 6) + 1}` : `chart${(value % 11) + 1}`
  ) as Color
}

const expectNever = (value: never): never => {
  throw new TypeError('Unexpected value: ' + value)
}

type RGBA = { r: number; g: number; b: number; a: number }

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
const clamp255 = (v: number) => Math.max(0, Math.min(255, v))

const parseColor = (input: string): RGBA => {
  const s = input.trim()

  // rgb / rgba
  const m = s.match(/^rgba?\((.+)\)$/i)
  if (m) {
    const parts = m[1].split(',').map((p) => p.trim())
    const r = Number(parts[0])
    const g = Number(parts[1])
    const b = Number(parts[2])
    const a = parts.length >= 4 ? Number(parts[3]) : 1
    return { r, g, b, a: clamp01(a) }
  }

  // hex #rgb / #rrggbb
  let hex = s.replace(/^#/, '')
  if (hex.length === 3)
    hex = hex
      .split('')
      .map((ch) => ch + ch)
      .join('')
  if (hex.length !== 6) throw new Error(`Unsupported color: ${input}`)

  const n = parseInt(hex, 16)
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
    a: 1,
  }
}

const rgbToHsl = ({ r, g, b, a }: RGBA) => {
  const R = r / 255,
    G = g / 255,
    B = b / 255
  const max = Math.max(R, G, B)
  const min = Math.min(R, G, B)
  const d = max - min

  let h = 0
  if (d !== 0) {
    if (max === R) h = ((G - B) / d) % 6
    else if (max === G) h = (B - R) / d + 2
    else h = (R - G) / d + 4
    h *= 60
    if (h < 0) h += 360
  }

  const l = (max + min) / 2
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1))

  return { h, s, l, a }
}

const hslToRgb = (h: number, s: number, l: number, a: number): RGBA => {
  const C = (1 - Math.abs(2 * l - 1)) * s
  const X = C * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - C / 2

  let rp = 0,
    gp = 0,
    bp = 0
  if (0 <= h && h < 60) [rp, gp, bp] = [C, X, 0]
  else if (60 <= h && h < 120) [rp, gp, bp] = [X, C, 0]
  else if (120 <= h && h < 180) [rp, gp, bp] = [0, C, X]
  else if (180 <= h && h < 240) [rp, gp, bp] = [0, X, C]
  else if (240 <= h && h < 300) [rp, gp, bp] = [X, 0, C]
  else [rp, gp, bp] = [C, 0, X]

  return {
    r: Math.round(clamp255((rp + m) * 255)),
    g: Math.round(clamp255((gp + m) * 255)),
    b: Math.round(clamp255((bp + m) * 255)),
    a: clamp01(a),
  }
}

const adjustColor = (
  color: string,
  mode: 'lighten' | 'darken',
  percent: number,
): string => {
  const p = clamp01(percent / 100)
  const rgba = parseColor(color)
  const { h, s, l, a } = rgbToHsl(rgba)

  const newL = mode === 'darken' ? l * (1 - p) : l + (1 - l) * p

  const out = hslToRgb(h, s, clamp01(newL), a)
  return `rgba(${out.r}, ${out.g}, ${out.b}, ${out.a})`
}

export { getColorForString, getChartColorForNumber, expectNever, adjustColor }
